import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { AlertTriangle, Check } from 'lucide-react'
import { useBatchClassify } from '../../hooks/useComments'
import type { NegotiationParty } from '../../services/negotiationsApi'

interface BatchClassifyBannerProps {
  negotiationId: number
  commentIds: number[]
  parties: NegotiationParty[]
}

export function BatchClassifyBanner({
  negotiationId,
  commentIds,
  parties,
}: BatchClassifyBannerProps) {
  const { t } = useTranslation()
  const batchClassify = useBatchClassify()
  const [visibility, setVisibility] = useState<'INTERNAL' | 'ALL' | 'SPECIFIC'>('INTERNAL')
  const [targetTag, setTargetTag] = useState('')

  const counterparties = parties.filter(
    (p) => (p.role === 'COUNTERPARTY' || p.role === 'CUSTOMER') && p.is_active,
  )

  if (commentIds.length === 0) return null

  const handleClassify = () => {
    batchClassify.mutate({
      negotiationId,
      commentIds,
      visibility,
      targetTag: visibility === 'SPECIFIC' ? targetTag : undefined,
    })
  }

  return (
    <div className="mx-3 my-1.5 p-2 rounded-md bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800">
      <div className="flex items-start gap-1.5">
        <AlertTriangle size={12} className="text-amber-500 shrink-0 mt-0.5" />
        <div className="flex-1 min-w-0">
          <p className="text-[10px] font-medium text-amber-700 dark:text-amber-400">
            {t('comments.batchClassifyTitle', { count: commentIds.length })}
          </p>
          <div className="flex items-center gap-1 mt-1.5">
            <select
              value={visibility === 'SPECIFIC' ? `SPECIFIC:${targetTag}` : visibility}
              onChange={(e) => {
                const val = e.target.value
                if (val.startsWith('SPECIFIC:')) {
                  setVisibility('SPECIFIC')
                  setTargetTag(val.slice(9))
                } else {
                  setVisibility(val as 'INTERNAL' | 'ALL')
                  setTargetTag('')
                }
              }}
              className="text-[10px] px-1.5 py-0.5 rounded border border-amber-300 dark:border-amber-700 bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300"
            >
              <option value="INTERNAL">{t('comments.modeInternal')}</option>
              <option value="ALL">{t('comments.modeAll')}</option>
              {counterparties.map((p) => (
                <option key={p.tag_name} value={`SPECIFIC:${p.tag_name}`}>
                  {p.party_label}
                </option>
              ))}
            </select>
            <button
              onClick={handleClassify}
              disabled={batchClassify.isPending || (visibility === 'SPECIFIC' && !targetTag)}
              className="flex items-center gap-0.5 px-1.5 py-0.5 text-[10px] font-medium rounded bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 transition-colors"
            >
              <Check size={10} />
              {t('comments.batchClassifyConfirm')}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
